import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { memberClient } from '../config/Apollo';
import { BannerState, InfoBanner } from '../components/Errors/InfoBanner';
import InvoicingForm from '../components/Invoicing/InvoicingForm';
import { InvoicingPageFormSubmitted } from '../components/Invoicing/InvoicingPageFormSubmitted';

export enum InvoicingPageTestingID {
  InvoicingPage = 'invoicing-page',
  InvoicingForm = 'invoicing-form',
}

// This page is only accessible to users with the providerInvoicingOnly property with the feature flag turned on.
export const InvoicingPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [infoBannerMessage, setInfoBannerMessage] = useState('');
  const [infoBannerState, setInfoBannerState] = useState(BannerState.Info);

  const { invoicingDashboard } = useFlags();

  if (!invoicingDashboard) {
    return null;
  }

  const setInfoBanner = (show, message = '', state = BannerState.Info) => {
    setShowInfoBanner(show);
    setInfoBannerMessage(message);
    setInfoBannerState(state);
  };

  const handleSessionDateChange = (date: Moment) => {
    const shouldShowInfoBanner = moment().diff(date, 'days') > 5;
    if (shouldShowInfoBanner) {
      setInfo(
        'To receive payments in a timely manner, send your invoices within 5 days of completing the session.',
      );
    }
  };

  const handleSuccess = () => {
    setInfoBanner(false);
    setSubmitted(true);
  };

  const setInfo = (message) => {
    setInfoBanner(true, message, BannerState.Info);
  };

  const setError = (message) => {
    setInfoBanner(true, message, BannerState.Error);
  };

  return (
    <>
      {submitted && <InvoicingPageFormSubmitted onPress={() => setSubmitted(false)} />}

      {!submitted && (
        <div data-testid={InvoicingPageTestingID.InvoicingPage} className="container mx-auto p-8">
          {/* TODO: when dialog component is reimplemented with tailwind, remove these padding  properties. */}
          <div className="ml-8 pl-1">
            {showInfoBanner && (
              <InfoBanner bannerState={infoBannerState} message={infoBannerMessage} />
            )}

            <h1 className="font-black text-gray-900 text-2xl">Invoice Submission</h1>

            <p className="text-gray-750 text-sm leading-normal ">
              We collect this information to understand how to improve our services. We keep your
              information confidential, and we don't use this as part of any evaluation. If you have
              questions or need additional help, email:{' '}
              <a
                className="text-blue-200 font-semibold underline"
                href="mailto:provider@joinmodernhealth.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                provider@joinmodernhealth.com.
              </a>
            </p>
          </div>

          <div data-testid={InvoicingPageTestingID.InvoicingForm}>
            <InvoicingForm
              hideSuccessSnackbar
              isProviderInvoicingOnly
              memberClient={memberClient}
              onSessionDateChange={handleSessionDateChange}
              onSuccess={handleSuccess}
              setError={setError}
              showSummaryModal
            />
          </div>
        </div>
      )}
    </>
  );
};
